// context/CartContext.js - UPDATED FOR REAL AUTHENTICATION ONLY
import React, { createContext, useContext, useReducer, useEffect } from 'react';
import toast from 'react-hot-toast';

const CartContext = createContext();

const cartReducer = (state, action) => {
    switch (action.type) {
        case 'SET_CART':
            return {
                ...state,
                items: action.payload.items || [],
                loading: false,
                error: null
            };
        case 'ADD_TO_CART':
            return {
                ...state,
                items: action.payload.items || []
            };
        case 'REMOVE_FROM_CART':
            return {
                ...state,
                items: action.payload.items || []
            };
        case 'UPDATE_QUANTITY':
            return {
                ...state,
                items: action.payload.items || []
            };
        case 'CLEAR_CART':
            return {
                ...state,
                items: []
            };
        case 'SET_LOADING':
            return {
                ...state,
                loading: action.payload,
                error: null
            };
        case 'SET_ERROR':
            return {
                ...state,
                loading: false,
                error: action.payload
            };
        default:
            return state;
    }
};

const initialState = {
    items: [], 
    loading: true,
    error: null
};

export const CartProvider = ({ children }) => {
    const [state, dispatch] = useReducer(cartReducer, initialState);
    const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

    // ✅ FIXED: Check for token in multiple possible locations
    const isAuthenticated = () => {
        // Check all possible token locations
        const possibleTokens = [
            localStorage.getItem('token'),
            localStorage.getItem('bloomsoko-token'),
            localStorage.getItem('userToken'),
            localStorage.getItem('authToken')
        ];
        return possibleTokens.some(token => token && token !== 'undefined');
    };

    // ✅ FIXED: Get auth headers WITHOUT userid header
    const getAuthHeaders = () => {
        const headers = {
            'Content-Type': 'application/json'
        };
        
        // Get token from any possible location
        const token = localStorage.getItem('token') || 
                     localStorage.getItem('bloomsoko-token') || 
                     localStorage.getItem('userToken') ||
                     localStorage.getItem('authToken');
        
        if (token && token !== 'undefined') {
            headers['Authorization'] = `Bearer ${token}`;
            console.log('🔐 Token found, adding to headers');
        } else {
            console.log('❌ No valid token found');
        }
        
        // 🔥 REMOVED: DO NOT send userid header anymore
        // Backend only accepts real authenticated users
        
        return headers;
    };

    // ✅ FIXED: Event listeners for cart refresh
    useEffect(() => {
        const handleCartRefresh = () => {
            console.log('🔄 Manual cart refresh triggered');
            fetchCart();
        };

        const handleCartUpdated = () => {
            console.log('🛒 Cart updated event received');
            fetchCart();
        };

        window.addEventListener('cartRefreshNeeded', handleCartRefresh);
        window.addEventListener('cartUpdated', handleCartUpdated);
        
        return () => {
            window.removeEventListener('cartRefreshNeeded', handleCartRefresh);
            window.removeEventListener('cartUpdated', handleCartUpdated);
        };
    }, []);

    // ✅ FIXED: Load cart from backend on mount
    useEffect(() => {
        const token = localStorage.getItem('token') || localStorage.getItem('bloomsoko-token');
        if (token && token !== 'undefined') {
            fetchCart();
        } else {
            console.log('⚠️ No auth token, skipping cart fetch');
            dispatch({ type: 'SET_CART', payload: { items: [] } });
        }
    }, []);

    // ✅ FIXED: Fetch cart with proper auth
    const fetchCart = async () => {
        try {
            if (!isAuthenticated()) {
                console.log('⚠️ Not authenticated, skipping cart fetch');
                dispatch({ type: 'SET_CART', payload: { items: [] } });
                return;
            }

            dispatch({ type: 'SET_LOADING', payload: true });
            
            console.log('🔄 Fetching cart with headers:', getAuthHeaders());
            
            const response = await fetch(`${API_URL}/cart`, {
                headers: getAuthHeaders()
            });
            
            console.log('📦 Cart response status:', response.status);
            
            if (response.status === 401) {
                // Unauthorized - clear token and cart
                console.log('❌ Token expired or invalid');
                localStorage.removeItem('token');
                localStorage.removeItem('bloomsoko-token');
                localStorage.removeItem('userToken');
                dispatch({ type: 'SET_CART', payload: { items: [] } });
                toast.error('Session expired. Please login again.');
                return;
            }
            
            if (!response.ok) {
                throw new Error(`HTTP ${response.status}: ${response.statusText}`);
            }
            
            const cartData = await response.json();
            console.log('🛒 Cart data received:', {
                success: cartData.success,
                itemsCount: cartData.data?.items?.length || 0,
                items: cartData.data?.items
            });
            
            if (cartData.success) {
                dispatch({ type: 'SET_CART', payload: cartData.data });
            } else {
                console.error('❌ Cart fetch failed:', cartData.message);
                dispatch({ type: 'SET_ERROR', payload: cartData.message });
            }
        } catch (error) {
            console.error('❌ Error fetching cart:', error);
            dispatch({ type: 'SET_ERROR', payload: error.message });
            dispatch({ type: 'SET_CART', payload: { items: [] } });
        }
    };

    // ✅ FIXED: Add to cart with better error handling
    const addToCart = async (product, quantity = 1, isBooking = false) => {
        try {
            // Check authentication
            if (!isAuthenticated()) {
                toast.error('Please login to add items to cart');
                // Redirect to login with return URL
                window.location.href = `/login?returnUrl=${encodeURIComponent(window.location.pathname)}`;
                return { success: false, message: 'Authentication required' };
            }

            console.log('🛒 Adding to cart:', {
                productId: product._id,
                productName: product.name,
                quantity,
                isBooking
            });

            const response = await fetch(`${API_URL}/cart/add`, {
                method: 'POST',
                headers: getAuthHeaders(),
                body: JSON.stringify({
                    productId: product._id,
                    quantity,
                    isBooking
                })
            });

            const result = await response.json();
            console.log('📦 Add to cart response:', result);

            if (!response.ok) {
                if (response.status === 401) {
                    // Token expired
                    localStorage.removeItem('token');
                    localStorage.removeItem('bloomsoko-token');
                    toast.error('Session expired. Please login again.');
                    window.location.href = '/login';
                }
                throw new Error(result.message || 'Failed to add to cart');
            }

            // Update cart state
            if (result.success && result.data) {
                dispatch({ type: 'ADD_TO_CART', payload: result.data });
                
                // Emit events for real-time updates
                window.dispatchEvent(new CustomEvent('inventoryUpdated', { 
                    detail: { productId: product._id } 
                }));
                window.dispatchEvent(new Event('cartUpdated'));
                
                toast.success(isBooking ? 'Product booked successfully!' : 'Product added to cart!');
                
                // Refresh cart to get latest state
                setTimeout(() => fetchCart(), 500);
                
                return { success: true, data: result.data };
            } else {
                throw new Error(result.message || 'Failed to add to cart');
            }
        } catch (error) {
            console.error('❌ Error adding to cart:', error);
            toast.error(error.message || 'Failed to add to cart');
            dispatch({ type: 'SET_ERROR', payload: error.message });
            return { success: false, message: error.message };
        }
    };

    // ✅ FIXED: Remove from cart
    const removeFromCart = async (itemId) => {
        try {
            const response = await fetch(`${API_URL}/cart/remove/${itemId}`, {
                method: 'DELETE',
                headers: getAuthHeaders()
            });

            const result = await response.json();
            
            if (response.ok && result.success) {
                // Fetch updated cart
                await fetchCart();
                window.dispatchEvent(new Event('cartUpdated'));
                toast.success('Item removed from cart');
            } else {
                throw new Error(result.message || 'Failed to remove item');
            }
        } catch (error) {
            console.error('❌ Error removing from cart:', error);
            toast.error(error.message || 'Failed to remove item');
        }
    };

    // ✅ FIXED: Update quantity
    const updateQuantity = async (itemId, quantity) => {
        try {
            if (quantity < 1) {
                await removeFromCart(itemId);
                return;
            }

            const response = await fetch(`${API_URL}/cart/update/${itemId}`, {
                method: 'PUT',
                headers: getAuthHeaders(),
                body: JSON.stringify({ quantity })
            });

            const result = await response.json();
            
            if (response.ok && result.success) {
                await fetchCart();
                window.dispatchEvent(new Event('cartUpdated'));
            } else {
                throw new Error(result.message || 'Failed to update quantity');
            }
        } catch (error) {
            console.error('❌ Error updating cart:', error);
            toast.error(error.message || 'Failed to update quantity');
        }
    };

    // ✅ FIXED: Clear cart
    const clearCart = async () => {
        try {
            const response = await fetch(`${API_URL}/cart/clear`, {
                method: 'DELETE',
                headers: getAuthHeaders()
            });

            if (response.ok) {
                const result = await response.json();
                if (result.success) {
                    dispatch({ type: 'CLEAR_CART' });
                    window.dispatchEvent(new Event('cartUpdated'));
                    toast.success('Cart cleared successfully');
                    return true;
                }
            }
            throw new Error('Failed to clear cart');
        } catch (error) {
            console.error('❌ Error clearing cart:', error);
            toast.error(error.message || 'Failed to clear cart');
            return false;
        }
    };

    // ✅ REMOVED: migrateCartToUser function - No more guest users
    // All carts are for authenticated users only

    const getCartCount = () => {
        if (!state.items || !Array.isArray(state.items)) {
            return 0;
        }
        return state.items.reduce((total, item) => total + (item.quantity || 0), 0);
    };

    const getCartTotal = () => {
        if (!state.items || !Array.isArray(state.items)) {
            return 0;
        }
        return state.items.reduce((total, item) => {
            const price = item.price || item.product?.price || 0;
            const quantity = item.quantity || 0;
            return total + (price * quantity);
        }, 0);
    };

    // Debug logging
    useEffect(() => {
        console.log('🔍 CART CONTEXT DEBUG:', {
            hasToken: isAuthenticated(),
            tokenSources: {
                token: localStorage.getItem('token'),
                bloomsokoToken: localStorage.getItem('bloomsoko-token'),
                userToken: localStorage.getItem('userToken'),
                authToken: localStorage.getItem('authToken')
            },
            cartItems: state.items?.length || 0,
            cartCount: getCartCount(),
            loading: state.loading,
            error: state.error
        });
    }, [state.items, state.loading, getCartCount]);

    return (
        <CartContext.Provider value={{
            cart: state.items || [],
            loading: state.loading,
            error: state.error,
            addToCart,
            removeFromCart,
            updateQuantity,
            clearCart,
            getCartCount,
            getCartTotal,
            refreshCart: fetchCart,
            isAuthenticated: isAuthenticated()
        }}>
            {children}
        </CartContext.Provider>
    );
};

export const useCart = () => {
    const context = useContext(CartContext);
    if (!context) {
        throw new Error('useCart must be used within a CartProvider');
    }
    return context;
};